import { useAddNonModelledFamilyType } from "@api/nonModelledFamily";
import DataGrid from "@components/DataGrid/DataGrid";
import { useMaterialApisByType } from "@hooks/useMaterialApisByType";
import i18n from "@i18n/i18n";
import AddIcon from "@mui/icons-material/Add";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import DeleteIcon from "@mui/icons-material/Delete";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Box, IconButton, Tooltip, Typography } from "@mui/material";
import { GridActionsCellItem, GridColDef, GridRenderCellParams, GridValidRowModel } from "@mui/x-data-grid-premium";
import { observer } from "mobx-react";
import { useEffect, useRef, useState } from "react";
import useResizeObserver from "use-resize-observer";
import AddModelTypeDialog from "./AddModelTypeDialog";

interface RebarMaterialGridProps {
  type: string;
  showModelTypeColumn: boolean;
}

const RebarMaterialGrid = observer(({ type, showModelTypeColumn }: RebarMaterialGridProps) => {
  const [rows, setRows] = useState<GridValidRowModel[]>([]);
  const [isAddTypeDialogOpen, setIsAddTypeDialogOpen] = useState(false);
  const [modelTypeInputs, setModelTypeInputs] = useState<string[]>([""]);
  const [selectedRowId, setSelectedRowId] = useState<string | number | null>(null);
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [duplicateErrors, setDuplicateErrors] = useState<Set<number>>(new Set());
  const containerRef = useRef(null);
  const { height } = useResizeObserver({ ref: containerRef });
  const { isRebar, useData, useDeleteRebar, useDeleteNonModelled } = useMaterialApisByType(type);

  const addFamilyTypeMutation = useAddNonModelledFamilyType({
    onSuccess: () => {
      console.log("Model family types added successfully");
      useData.refetch?.();
      handleCloseDialog();
    },
    onError: (error: any) => {
      console.error("Error adding model family types:", error);
      alert(`Error adding model family types: ${error.message || "Unknown error"}`);
    },
  });

  const materials = useData?.data || [];

  const toggleRowExpansion = (rowId: string) => {
    setExpandedRows((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(rowId)) {
        newSet.delete(rowId);
      } else {
        newSet.add(rowId);
      }
      return newSet;
    });
  };

  const getExistingChildEntries = (parentId: string | number): string[] => {
    return rows.filter((row) => row.parentId === parentId.toString() && !row.isParent).map((row) => row.value.toLowerCase().trim());
  };

  const validateDuplicates = (inputs: string[]): Set<number> => {
    const duplicateIndexes = new Set<number>();
    const existingEntries = selectedRowId ? getExistingChildEntries(selectedRowId) : [];

    inputs.forEach((input, index) => {
      const trimmedInput = input.toLowerCase().trim();

      if (trimmedInput === "") return;
      if (existingEntries.includes(trimmedInput)) {
        duplicateIndexes.add(index);
        return;
      }

      const duplicateInInputs = inputs.some((otherInput, otherIndex) => otherIndex !== index && otherInput.toLowerCase().trim() === trimmedInput && otherInput.trim() !== "");

      if (duplicateInInputs) {
        duplicateIndexes.add(index);
      }
    });

    return duplicateIndexes;
  };

  const handleInputChange = (index: number, value: string) => {
    const newInputs = modelTypeInputs.map((input, i) => (i === index ? value : input));
    setModelTypeInputs(newInputs);

    const duplicates = validateDuplicates(newInputs);
    setDuplicateErrors(duplicates);
  };

  const handleAddInput = () => {
    const newInputs = [...modelTypeInputs, ""];
    setModelTypeInputs(newInputs);

    const duplicates = validateDuplicates(newInputs);
    setDuplicateErrors(duplicates);
  };

  const handleRemoveInput = (index: number) => {
    if (modelTypeInputs.length > 1) {
      const newInputs = modelTypeInputs.filter((_, i) => i !== index);
      setModelTypeInputs(newInputs);

      const duplicates = validateDuplicates(newInputs);
      setDuplicateErrors(duplicates);
    }
  };

  useEffect(() => {
    if (showModelTypeColumn && materials.length > 0) {
      const newRows: GridValidRowModel[] = [];

      materials.forEach((family: any) => {
        const familyId = family.metadataEntityId?.toString() || "";
        if (!familyId) return;

        const familyTypes = family.modelFamilyTypes || family.children || [];

        newRows.push({
          id: familyId,
          value: family.name,
          type: family.type,
          isParent: true,
          parentId: null,
          level: 0,
          hasChildren: familyTypes.length > 0,
        });

        familyTypes.forEach((familyType: any) => {
          const childId = familyType.metadataEntityId?.toString() || "";
          if (!childId) return;

          newRows.push({
            id: childId,
            value: familyType.name,
            type: familyType.type,
            isParent: false,
            parentId: familyId,
            level: 1,
            hasChildren: false,
          });
        });
      });

      setRows(newRows);
      setExpandedRows(new Set());
    } else {
      const newRows: GridValidRowModel[] = materials.map((item: any, index: number) => {
        if (item?.metadataEntityId !== undefined && item?.name !== undefined) {
          return {
            id: item.metadataEntityId.toString(),
            value: item.name,
            type: item.type || type,
            level: 0,
            isParent: false,
            hasChildren: false,
          };
        } else if (item?.id !== undefined && item?.name !== undefined) {
          return {
            id: item.id.toString(),
            value: item.name,
            type,
            level: 0,
            isParent: false,
            hasChildren: false,
          };
        } else {
          return {
            id: index.toString(),
            value: item.value || item,
            type,
            level: 0,
            isParent: false,
            hasChildren: false,
          };
        }
      });

      setRows(newRows);
      setExpandedRows(new Set());
    }
  }, [materials, type, showModelTypeColumn]);

  const visibleRows = showModelTypeColumn
    ? rows.filter((row) => {
        if (row.isParent) return true;
        if (row.parentId && expandedRows.has(row.parentId)) {
          return true;
        }
        return false;
      })
    : rows;

  const handleDeleteClick = async (params: any) => {
    const { id, value, isParent } = params.row;

    try {
      if (!isRebar && typeof id === "string" && id.trim() !== "") {
        await useDeleteNonModelled.mutateAsync({ id });
      } else {
        await useDeleteRebar.mutateAsync({ value, type });
      }

      await useData.refetch?.();
    } catch (err) {
      console.error("Delete failed:", err);
    }
  };

  const handleAddModelType = (rowId: string | number) => {
    setSelectedRowId(rowId);
    setModelTypeInputs([""]);
    setDuplicateErrors(new Set());
    setIsAddTypeDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setIsAddTypeDialogOpen(false);
    setModelTypeInputs([""]);
    setDuplicateErrors(new Set());
    setSelectedRowId(null);
  };

  const handleSaveModelType = async () => {
    if (selectedRowId === null) return;

    const modelTypes = modelTypeInputs.filter((value) => value.trim() !== "");

    if (modelTypes.length === 0) {
      alert(i18n.t("rebar-material-grid.add-model-type-dialog.validation.empty-list"));
      return;
    }

    const duplicates = validateDuplicates(modelTypeInputs);
    if (duplicates.size > 0) {
      alert(i18n.t("rebar-material-grid.add-model-type-dialog.validation.duplicate-entries"));
      return;
    }

    try {
      const payload = {
        ModelFamilyId: selectedRowId.toString(),
        ModelFamilyTypeList: modelTypes,
      };
      await addFamilyTypeMutation.mutateAsync(payload);
    } catch (err) {
      console.error("Error adding model types:", err);
    }
  };

  const hasAnyValue = modelTypeInputs.some((value) => value.trim() !== "");
  const hasValidationErrors = duplicateErrors.size > 0;

  const ValueCell = ({ row }: GridRenderCellParams) => {
    const isExpanded = expandedRows.has(row.id.toString());

    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          pl: (row.level || 0) * 4,
          height: "100%",
        }}
      >
        {row.isParent && row.hasChildren && (
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              toggleRowExpansion(row.id.toString());
            }}
            sx={{ p: 0.25, mr: 0.5 }}
          >
            {isExpanded ? <ExpandMoreIcon sx={{ fontSize: 16, color: "action.active" }} /> : <ChevronRightIcon sx={{ fontSize: 16, color: "action.active" }} />}
          </IconButton>
        )}
        {row.isParent && !row.hasChildren && <Box sx={{ width: 24, mr: 0.5 }} />}
        {!row.isParent && <Box sx={{ width: 24, mr: 0.5 }} />}
        <Typography
          variant="body2"
          sx={{
            fontWeight: "normal",
            color: "text.primary",
            flexGrow: 1,
          }}
        >
          {row.value}
        </Typography>
      </Box>
    );
  };

  const treeColumns: GridColDef[] = [
    {
      field: "id",
      headerName: "ID",
      hideable: false,
    },
    {
      field: "value",
      headerName: i18n.t("rebar-material-grid.columns.Name.value"),
      editable: false,
      flex: 1,
      sortable: false,
      renderCell: (params) => <ValueCell {...params} />,
    },
    {
      field: "add-model-type",
      headerName: i18n.t("rebar-material-grid.columns.Name.add-model-type"),
      width: 120,
      sortable: false,
      disableColumnMenu: true,
      renderCell: (params) => {
        if (!params.row.isParent) return null;

        return (
          <Box sx={{ display: "flex", justifyContent: "center", width: "100%" }}>
            <Tooltip title={i18n.t("rebar-material-grid.add-model-type-button.tooltip")}>
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  handleAddModelType(params.row.id);
                }}
                sx={{ padding: 2 }}
                disabled={addFamilyTypeMutation.isPending}
              >
                <AddIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        );
      },
    },
    {
      field: "type",
      headerName: i18n.t("rebar-material-grid.columns.Name.type"),
      flex: 1,
      editable: false,
      sortable: false,
    },
    {
      field: "actions",
      type: "actions",
      headerName: i18n.t("rebar-material-grid.columns.Name.action"),
      width: 100,
      sortable: false,
      getActions: (params) => [
        <GridActionsCellItem
          key={`delete-${params.id}`}
          icon={
            <Tooltip title={i18n.t("rebar-material-grid.delete-button.tooltip")}>
              <DeleteIcon />
            </Tooltip>
          }
          label={i18n.t("rebar-material-grid.delete-button.label")}
          onClick={() => handleDeleteClick(params)}
        />,
      ],
    },
  ];

  const baseColumns: GridColDef[] = [
    {
      field: "id",
      headerName: "ID",
      hideable: false,
    },
    {
      field: "value",
      headerName: i18n.t("rebar-material-grid.columns.Name.value"),
      editable: false,
      flex: 1,
    },
    {
      field: "type",
      headerName: i18n.t("rebar-material-grid.columns.Name.type"),
      flex: 1,
      editable: false,
    },
    {
      field: "actions",
      type: "actions",
      headerName: i18n.t("rebar-material-grid.columns.Name.action"),
      width: 100,
      getActions: (params) => [
        <GridActionsCellItem
          key={`delete-${params.id}`}
          icon={
            <Tooltip title={i18n.t("rebar-material-grid.delete-button.tooltip")}>
              <DeleteIcon />
            </Tooltip>
          }
          label={i18n.t("rebar-material-grid.delete-button.label")}
          onClick={() => handleDeleteClick(params)}
        />,
      ],
    },
  ];

  const columns: GridColDef[] = showModelTypeColumn ? treeColumns : baseColumns;

  const columnVisibilityModel = {
    id: false,
  };

  return (
    <Box ref={containerRef} sx={{ width: "100%", height: "100%" }}>
      <DataGrid
        loading={useData?.isFetching || useData?.isPending}
        columns={columns}
        rows={visibleRows}
        disableColumnMenu
        disableRowSelectionOnClick
        disableColumnSorting={showModelTypeColumn}
        columnVisibilityModel={columnVisibilityModel}
        sx={{ height: `${(height || 400) - 16}px` }}
      />
      <AddModelTypeDialog
        open={isAddTypeDialogOpen}
        inputs={modelTypeInputs}
        onInputChange={handleInputChange}
        onAddInput={handleAddInput}
        onRemoveInput={handleRemoveInput}
        onClose={handleCloseDialog}
        onSave={handleSaveModelType}
        isSaving={addFamilyTypeMutation.isPending}
        duplicateErrors={duplicateErrors}
        selectedRowId={selectedRowId}
        getExistingChildEntries={getExistingChildEntries}
      />
    </Box>
  );
});

export default RebarMaterialGrid;
